import { MessagePlugin } from 'tdesign-vue-next';
import { FetchSSEOptions, SSEEvent } from './type';

interface RequestParams {
  url: string;
  data: any;
  success: (res: any) => void;
  fail?: () => void;
  complete?: (isOk: Boolean, msg?: String, requestid?: String) => void;
  cancel?: (res: any) => void;
  abort?: () => void;
}
/**
 * Sends a Server-Sent Events (SSE) request to the specified URL with the given data and callback function.
 * @param url - The URL to send the SSE request to.
 * @param data - The data to send with the SSE request.
 * @param callback - The function to call when the SSE response is received.
 * @returns void
 */
export async function sseRequest(params: RequestParams, apiKey?: string) {
  const { url, data, complete, cancel, abort } = params;
  const controller = new AbortController();
  const { signal } = controller;
  cancel?.(controller);
  const tokenHeaders: any = {
    'Content-Type': 'application/json',
  };
  tokenHeaders.Authorization = `Bearer${apiKey ? ` ${apiKey}` : ''}`;
  const responsePromise = fetch(url, {
    method: 'POST',
    headers: tokenHeaders,
    body: JSON.stringify(data),
    signal: controller.signal,
  }).catch((err) => {
    // 捕获接口异常
    const msg = err.toString() || '流式接口异常';
    complete?.(false, msg);
    return Promise.reject(err); // 确保错误能够被后续的.catch()捕获
  });
  fetchSSE(() => responsePromise, params);
  signal.addEventListener('abort', () => {
    try {
      abort && abort();
    } catch {}
  });
}

export const fetchSSE = async (fetchFn: () => Promise<Response>, options: FetchSSEOptions = {}) => {
  const { success, fail, complete } = options;
  const responsePromise = fetchFn().catch((e) => {
    const msg = e.toString() || '流式接口异常';
    complete?.(false, msg);
    return Promise.reject(e); // 确保错误能够被后续的.catch()捕获
  });

  responsePromise
    .then((response) => {
      // 后端需要上报和告警接口的错误，把程序错误的http状态码定义为了500
      if (response.status === 200 || response.status === 500) {
        // ReadableStream解码为文本
        const reader = response?.body?.getReader();
        const decoder = new TextDecoder();
        if (!reader) throw new Error('No reader available');
        const bufferArr: string[] = [];
        let dataText = ''; // 记录数据
        const event: SSEEvent = { type: null, data: null };

        async function processText({ done, value }: ReadableStreamReadResult<Uint8Array>): Promise<void> {
          if (done) {
            // 正常的返回
            complete?.(true, '');
            return Promise.resolve();
          }
          const chunk = decoder.decode(value, { stream: true });
          const buffers = chunk.toString().split(/\r?\n/);
          bufferArr.push(...buffers);
          const i = 0;
          while (i < bufferArr.length) {
            const line = bufferArr[i];
            if (line) {
              dataText = dataText + line;
              if (line.startsWith('event:')) {
                // 是事件
                event.type = dataText.slice(6).trim();
                dataText = '';
              } else if (
                line.endsWith('"finished": false}') ||
                line.endsWith('"delta": "[EOS]"}') ||
                line.endsWith('"finished": true}') ||
                line.endsWith('"finished":true}')
              ) {
                event.data = JSON.parse(dataText.slice(5).trim());
                dataText = '';
              }
            }
            if (event.type && event.data) {
              const jsonData = JSON.parse(JSON.stringify(event));
              success(jsonData);
              if (response.status === 500) {
                const { retmsg } = JSON.parse(jsonData.data.response);
                MessagePlugin.error(retmsg);
              }
              event.type = null;
              event.data = null;
            }
            bufferArr.splice(i, 1);
          }
          return reader.read().then(processText);
        }
        return reader.read().then(processText);
      }
      // 传入错误信息排查
      complete?.(false, response.statusText || `暂无内容返回`);
    })
    .catch(() => {
      // 处理整个链式调用过程中发生的任何错误
      fail?.();
    });
};
